/**
 * The wave that spreads from where a button is pressed.
 *
 * The Ynex template marks its buttons for this effect ("waves-effect") but its React
 * version ships nothing to draw it, so a press gave no sign it had been felt. This is
 * that effect, in a few lines instead of a library: one listener for the whole page, a
 * circle that grows from the point pressed and fades, then removes itself.
 *
 * The circle sits in its own clipped layer inside the button rather than clipping the
 * button, because some buttons carry their tooltip inside them and clipping the button
 * would cut the tooltip off. Nothing is drawn for someone who has asked their device for
 * reduced motion.
 */

const PRESSABLE = ".ti-btn, .side-menu__item, .page-link, [role=tab]";

export function startRipple(): void {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

  document.addEventListener(
    "pointerdown",
    (event) => {
      if (reduced.matches || event.button !== 0) return;
      const target = (event.target as Element | null)?.closest<HTMLElement>(PRESSABLE);
      if (!target || target.matches(":disabled, [aria-disabled=true]")) return;

      const box = target.getBoundingClientRect();
      const size = Math.max(box.width, box.height) * 2;

      const layer = document.createElement("span");
      layer.className = "ohc-ripple-layer";
      const wave = document.createElement("span");
      wave.className = "ohc-ripple";
      wave.style.width = wave.style.height = `${size}px`;
      wave.style.left = `${event.clientX - box.left - size / 2}px`;
      wave.style.top = `${event.clientY - box.top - size / 2}px`;
      layer.appendChild(wave);
      target.appendChild(layer);

      wave.addEventListener("animationend", () => layer.remove(), { once: true });
      // In case the animation never runs (a tab hidden mid-press), clean up anyway.
      window.setTimeout(() => layer.remove(), 800);
    },
    { passive: true },
  );
}
